// searchBarReact.jsx
import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format } from 'date-fns';

// Replace these with your actual svgs/constants paths if different
import { ReactComponent as LocationIcon } from '../icons/location-icon.svg';
import { ReactComponent as CalendarIcon } from '../icons/calendar-icon.svg';
import { ReactComponent as EventIcon } from '../icons/event-icon.svg';
import { ReactComponent as ArrowDownIcon } from '../icons/arrow-down-icon.svg';
import { ReactComponent as ArrowRightIcon } from '../icons/arrow-right-icon.svg';
import { EVENT_TYPES, searchOptions, defaultSearchOptions } from '../../constants/realVenueData';

const SearchBarReact = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();

  // refs
  const locationRef = useRef(null);
  const dateRef = useRef(null);

  // state
  const [searchData, setSearchData] = useState({ location: '', date: null, eventType: '' });
  const [validationErrors, setValidationErrors] = useState({ location: false, date: false });

  // location suggestions
  const [locationSuggestions, setLocationSuggestions] = useState(defaultSearchOptions);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // date popper open
  const [dateOpen, setDateOpen] = useState(false);

  // calendar month state (controls DayPicker single-month rendering)
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // mobile sheet
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileField, setMobileField] = useState(null); // 'location' | 'date' | 'event'

  useEffect(() => {
    // initialize from URL params if present
    const urlLocation = searchParams.get('location') || '';
    const urlDate = searchParams.get('date') || '';
    const urlEventType = searchParams.get('eventType') || '';
    setSearchData({
      location: urlLocation,
      date: urlDate ? new Date(urlDate) : null,
      eventType: urlEventType,
    });
  }, [searchParams]);

  // helpers
  const openLocation = () => {
    if (isMobile) {
      setMobileField('location');
      setMobileOpen(true);
      return;
    }
    // Always use the ref directly to ensure correct anchor
    if (locationRef.current) {
      setShowSuggestions(true);
    }
  };
  const closeLocation = () => {
    setShowSuggestions(false);
  };

  const handleLocationClickAway = (event) => {
    if (locationRef.current && locationRef.current.contains(event.target)) {
      return;
    }
    closeLocation();
  };

  const handleLocationChange = (e) => {
    const v = e.target.value;
    setSearchData(prev => ({ ...prev, location: v }));
    setValidationErrors(prev => ({ ...prev, location: false }));
    if (!v.trim()) {
      setLocationSuggestions(defaultSearchOptions);
      setShowSuggestions(true);
    } else {
      const filtered = searchOptions.filter(o => o.toLowerCase().includes(v.toLowerCase())).slice(0, 6);
      setLocationSuggestions(filtered);
      setShowSuggestions(true);
    }
  };

  const handleLocationSelect = (loc) => {
    setSearchData(prev => ({ ...prev, location: loc }));
    setValidationErrors(prev => ({ ...prev, location: false }));
    closeLocation();
    if (isMobile) {
      setMobileOpen(false);
      setMobileField(null);
    }
  };

  const handleDateSelect = (d) => {
    if (!d) return; // Don't update if no date selected
    setSearchData(prev => ({ ...prev, date: d }));
    setValidationErrors(prev => ({ ...prev, date: false }));
    setDateOpen(false);
    if (isMobile) {
      setMobileOpen(false);
      setMobileField(null);
    }
  };

  const handleEventChange = (e) => {
    setSearchData(prev => ({ ...prev, eventType: e.target.value }));
  };

  const validateAndSubmit = (e) => {
    if (e) e.preventDefault();
    
    try {
      // Validate required fields
      const errs = {
        location: !searchData.location || !String(searchData.location).trim(),
        date: !searchData.date,
      };
      
      setValidationErrors(errs);
      
      // Check if there are any validation errors
      if (errs.location || errs.date) {
        return false; // Don't proceed with search
      }
      
      // Navigate to search results with search parameters
      const params = new URLSearchParams({
        location: searchData.location,
        date: searchData.date ? format(searchData.date, 'yyyy-MM-dd') : '',
        eventType: searchData.eventType || '',
      });
      
      navigate(`/search-results?${params.toString()}`);
      return true;
    } catch (error) {
      // Still try to navigate even if there's an error
      try {
        const params = new URLSearchParams({
          location: searchData.location || '',
          date: searchData.date ? format(searchData.date, 'yyyy-MM-dd') : '',
          eventType: searchData.eventType || '',
        });
        navigate(`/search-results?${params.toString()}`);
      } catch (navError) {
      }
      return false;
    }
  };

  // click away to close poppers
  useEffect(() => {
    const onDocClick = (ev) => {
      // Check if click is inside date picker calendar
      const isInsideDatePicker = ev.target.closest('.rdp');
      
      // Check if click is inside location ref
      const isInsideLocation = locationRef.current && locationRef.current.contains(ev.target);
      
      // Check if click is inside date ref  
      const isInsideDate = dateRef.current && dateRef.current.contains(ev.target);
      
      if (!isInsideDatePicker && !isInsideLocation && !isInsideDate) {
        setDateOpen(false);
        closeLocation();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const wrapperClass = embedded
    ? 'relative w-full z-10'
    : 'lg:absolute relative w-full md:px-[5rem] left-0 right-0 py-12 lg:py-0 mt-0 lg:mt-[-40px] z-10';

  return (
    <Box
      component={'section'}
      className={wrapperClass}
    >
      {/* Desktop layout */}
      {!isMobile && (
        <Paper
          component="form"
          onSubmit={validateAndSubmit}
          elevation={0}
          className='card-style'
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            position: 'relative',
            overflow: 'visible',
          }}
        >
          {/* Location */}
          <Box 
            ref={locationRef} 
            sx={{ 
              flex: '1 1 0%', 
              minWidth: 0, 
              position: 'relative',
              overflow: 'visible',
              zIndex: 1,
              ...(validationErrors.location && {
                bgcolor: '#FFF5F5',
                borderLeft: '3px solid #E53E3E',
              })
            }}
          >
            <TextField
              fullWidth
              value={searchData.location}
              onChange={handleLocationChange}
              placeholder="Where is your event?"
              onFocus={openLocation}
              onClick={openLocation}
              error={validationErrors.location}
              InputProps={{
                startAdornment: (<InputAdornment position="start" sx={{ ml: 1 }}><LocationIcon style={{ width: 22, height: 22 }} /></InputAdornment>),
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 1 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openLocation(); }}>
                      <ArrowDownIcon style={{ width: 14, height: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { px: 0 },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: 0,
                  bgcolor: 'transparent',
                },
                boxSizing: 'border-box',
                '& .MuiOutlinedInput-root': {
                    border: 'none',
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: 'none' },
                  }
              }}
            />

            {/* Location popper */}
            {showSuggestions && locationRef.current && (
              <Popper 
                open={true} 
                anchorEl={locationRef.current} 
                placement="bottom-start" 
                disablePortal={true}
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 4],
                    },
                  },
                  {
                    name: 'preventOverflow',
                    enabled: true,
                    options: {
                      rootBoundary: 'viewport',
                      tether: false,
                    },
                  },
                  {
                    name: 'flip',
                    enabled: true,
                  },
                ]}
                style={{ 
                  zIndex: 1400, 
                  width: locationRef.current?.offsetWidth || 'auto',
                }}
              >
              <ClickAwayListener onClickAway={handleLocationClickAway}>
                <Paper sx={{ mt: 1, borderRadius: '8px', overflow: 'hidden', border: '1px solid #D3D4D6' }}>
                  <List>
                    <ListItem button onClick={() => handleLocationSelect('Current location')} sx={{ bgcolor: 'rgba(4,98,66,0.05)' }}>
                      <LocationIcon style={{ width: 18, height: 18, marginRight: 12 }} />
                      <ListItemText primary="Current location" primaryTypographyProps={{ fontWeight: 600, color: '#046242' }} />
                    </ListItem>
                    {locationSuggestions.map((loc, i) => (
                      <ListItem key={i} button onClick={() => handleLocationSelect(loc)}>
                        <ListItemText primary={loc} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </ClickAwayListener>
              </Popper>
            )}
          </Box>

          <Divider orientation="vertical" flexItem  />

          {/* Date (desktop) */}
          <Box 
            ref={dateRef} 
            sx={{ 
              flex: '1 1 0%', 
              minWidth: 0, 
              position: 'relative',
              ...(validationErrors.date && {
                bgcolor: '#FFF5F5',
                borderLeft: '3px solid #E53E3E',
              })
            }}
          >
            <TextField
              fullWidth
              value={searchData.date ? format(searchData.date, 'MMM d, yyyy') : ''}
              placeholder="When?"
              onClick={() => setDateOpen(true)}
              InputProps={{
                startAdornment: (<InputAdornment position="start" sx={{ ml: 1 }}><CalendarIcon style={{ width: 20, height: 20 }} /></InputAdornment>),
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 1 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDateOpen(prev => !prev); }}>
                      <ArrowDownIcon style={{ width: 14, height: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ),
                readOnly: true,
                sx: { px: 0 },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: 0,
                  bgcolor: 'transparent',
                },
                boxSizing: 'border-box',
                '& .MuiOutlinedInput-root': {
                  border: 'none',
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' },
                }
              }}
              error={validationErrors.date}
            />

            {/* ---------- FIXED/NARROW Popper with single-month DayPicker ---------- */}
            <Popper
              open={dateOpen}
              anchorEl={dateRef.current}
              placement="bottom-start"
              disablePortal
              style={{ zIndex: 1400 }}
            >
              <ClickAwayListener onClickAway={() => setDateOpen(false)}>
                <Paper
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    boxSizing: 'border-box',
                    borderRadius: '8px',
                    border: '1px solid #D3D4D6',
                    mt: 1,
                  }}
                >
                <Box sx={{ px: 2, pt: 1, pb: 1 }}>
                  {/* Custom header (we remove DayPicker caption/navbar) */}
                  {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                      {calendarMonth ? format(calendarMonth, 'MMMM yyyy') : format(new Date(), 'MMMM yyyy')}
                    </Typography>

                    <Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCalendarMonth(prev => addMonths(prev, -1));
                        }}
                        aria-label="Previous month"
                      >
                        ‹
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCalendarMonth(prev => addMonths(prev, 1));
                        }}
                        aria-label="Next month"
                      >
                        ›
                      </IconButton>
                    </Box>
                  </Box> */}

                  {/* Scoped compact DayPicker */}
                  <Box className="" sx={{ width: '100%', boxSizing: 'border-box' }}>
                    <DayPicker
                      month={calendarMonth}
                      onMonthChange={(m) => setCalendarMonth(m)}
                      numberOfMonths={1}
                      mode="single"
                      selected={searchData.date}
                      onSelect={(d) => { handleDateSelect(d); }}
                      fromDate={new Date()}
                      showOutsideDays={false}
                      fixedWeeks={false}
                      disabled={{ before: new Date() }}
                      components={{
                        // Caption: () => null,
                        // Navbar: () => null,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                    border: 'none',
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: 'none' },
                  }
                      }}
                      styles={{
                        root: {
                          '--rdp-accent-color': theme.palette.primary.main,
                          '--rdp-accent-color-dark': theme.palette.primary.dark,
                          '--rdp-accent-color-light': theme.palette.primary.light,
                          '--rdp-background-color': theme.palette.background.paper,
                          color: theme.palette.text.primary,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Paper>
              </ClickAwayListener>
            </Popper>
          </Box>

          <Divider orientation="vertical" flexItem  />

          {/* Event type */}
          <Box sx={{ flex: '1 1 0%', minWidth: 0, position: 'relative' }}>
            <TextField
              select
              fullWidth
              value={searchData.eventType}
              onChange={handleEventChange}
              placeholder="What are you planning?"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <EventIcon style={{ width: 22, height: 22 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 1 }}>
                    <ArrowDownIcon style={{ width: 14, height: 14 }} />
                  </InputAdornment>
                ),
                sx: { px: 0 },
              }}
              SelectProps={{
                displayEmpty: true,
                IconComponent: () => null,
                MenuProps: { disablePortal: true },
                renderValue: (value) =>
                  value && value.length > 0
                    ? value
                    : <span style={{ color: '#CECECE' }}>What are you planning?</span>,
              }}
              sx={{
                '& .MuiInputBase-root': {
                  borderRadius: 0,
                  bgcolor: 'transparent',
                },
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  pl: 0,
                  pr: 0,
                  fontFamily: 'Inter, sans-serif',
                },
                '& .MuiOutlinedInput-root': {
                  border: 'none',
                  '& fieldset': { border: 'none' },
                  '&:hover fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': { border: 'none' },
                },
              }}
            >
              {EVENT_TYPES.map((et) => (
                <MenuItem key={et} value={et}>
                  {et}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Search button */}
          <Box sx={{ 
            flex: '0 0 auto', 
            bgcolor: 'white',
            p: '12px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '8px',
          }}>
            <Button
              variant="contained"
              onClick={validateAndSubmit}
              endIcon={<ArrowRightIcon style={{ width: 19, height: 14 }} />}
              sx={{
                bgcolor: '#046242',
                fontWeight: 500,
                fontFamily: 'Inter, sans-serif',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { 
                  bgcolor: '#035c3f',
                  boxShadow: 'none',
                },
                minWidth: 'auto',
                height: 'auto',
              }}
            >
              Search
            </Button>
          </Box>
        </Paper>
      )}

      {/* Mobile layout */}
      {isMobile && (
        <Box sx={{ maxWidth: 440, margin: '0 auto' }}>
          <Paper 
            elevation={0} 
            sx={{ 
              borderRadius: '8px', 
              border: '1px solid #D3D4D6', 
              overflow: 'hidden',
              bgcolor: 'white',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {/* Location */}
              <Box 
                onClick={() => { setMobileField('location'); setMobileOpen(true); }}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '16px',
                  p: '16px',
                  cursor: 'pointer',
                  bgcolor: validationErrors.location ? '#FFF5F5' : 'white',
                  borderLeft: validationErrors.location ? '3px solid #E53E3E' : 'none',
                  '&:hover': { bgcolor: validationErrors.location ? '#FFF5F5' : '#fafafa' }
                }}
              >
                <LocationIcon style={{ width: 28, height: 28, flexShrink: 0 }} />
                <Typography sx={{ 
                  fontSize: '16px', 
                  fontWeight: 500, 
                  fontFamily: 'Inter, sans-serif',
                  color: searchData.location ? '#000' : '#CECECE',
                  flex: 1,
                }}>
                  {searchData.location || 'Where is your event?'}
                </Typography>
              </Box>

              <Divider sx={{ borderColor: '#f7f7f7' }} />

              {/* Date */}
              <Box 
                onClick={() => { setMobileField('date'); setMobileOpen(true); }}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '16px',
                  p: '16px',
                  cursor: 'pointer',
                  bgcolor: validationErrors.date ? '#FFF5F5' : 'white',
                  borderLeft: validationErrors.date ? '3px solid #E53E3E' : 'none',
                  '&:hover': { bgcolor: validationErrors.date ? '#FFF5F5' : '#fafafa' }
                }}
              >
                <CalendarIcon style={{ width: 28, height: 28, flexShrink: 0 }} />
                <Typography sx={{ 
                  fontSize: '16px', 
                  fontWeight: 500, 
                  fontFamily: 'Inter, sans-serif',
                  color: searchData.date ? '#000' : '#CECECE',
                  flex: 1,
                }}>
                  {searchData.date ? format(searchData.date, 'MMM d, yyyy') : 'When?'}
                </Typography>
                <ArrowDownIcon style={{ width: 16, height: 16, flexShrink: 0 }} />
              </Box>

              <Divider sx={{ borderColor: '#f7f7f7' }} />

              {/* Event Type */}
              <Box 
                onClick={() => { setMobileField('event'); setMobileOpen(true); }}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '16px',
                  p: '16px',
                  cursor: 'pointer',
                  bgcolor: 'white',
                  '&:hover': { bgcolor: '#fafafa' }
                }}
              >
                <EventIcon style={{ width: 28, height: 28, flexShrink: 0 }} />
                <Typography sx={{ 
                  fontSize: '16px', 
                  fontWeight: 500, 
                  fontFamily: 'Inter, sans-serif',
                  color: searchData.eventType ? '#000' : '#CECECE',
                  flex: 1,
                }}>
                  {searchData.eventType || 'What are you planning?'}
                </Typography>
                <ArrowDownIcon style={{ width: 16, height: 16, flexShrink: 0 }} />
              </Box>

              <Divider sx={{ borderColor: '#f7f7f7' }} />

              {/* Search Button */}
              <Box sx={{ p: '12px', bgcolor: 'white' }}>
                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={validateAndSubmit}
                  sx={{ 
                    bgcolor: '#046242',
                    borderRadius: '8px',
                    px: '16px',
                    py: '12px',
                    fontSize: '16px',
                    fontWeight: 500,
                    fontFamily: 'Inter, sans-serif',
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { 
                      bgcolor: '#035c3f',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Search
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Mobile bottom sheet */}
      {mobileOpen && (
        <Box
          onClick={() => { setMobileOpen(false); setMobileField(null); }}
          sx={{ position: 'fixed', inset: 0, zIndex: 1500, backgroundColor: 'rgba(0,0,0,0.4)', display: { xs: 'block', lg: 'none' } }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              maxWidth: '100vw',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              bgcolor: 'background.paper',
              p: 2,
              maxHeight: '80vh',
              overflow: 'auto',
              boxSizing: 'border-box',
            }}
          >
            {/* location modal */}
            {mobileField === 'location' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon />
                  <TextField
                    autoFocus
                    placeholder="Where to?"
                    value={searchData.location}
                    onChange={handleLocationChange}
                    fullWidth
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                  />
                </Box>
                <List>
                  <ListItem button onClick={() => handleLocationSelect('Current location')}>
                    <LocationIcon style={{ width: 18, height: 18, marginRight: 12 }} />
                    <ListItemText primary="Current location" />
                  </ListItem>
                  {locationSuggestions.map((loc, i) => (
                    <ListItem key={i} button onClick={() => handleLocationSelect(loc)}>
                      <ListItemText primary={loc} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* date modal */}
            {mobileField === 'date' && (
              <Box>
                <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                  <DayPicker
                    mode="single"
                    selected={searchData.date}
                    onSelect={(d) => { handleDateSelect(d); setMobileOpen(false); setMobileField(null); }}
                    fromDate={new Date()}
                    disabled={{ before: new Date() }}
                    styles={{
                      root: {
                        '--rdp-accent-color': theme.palette.primary.main,
                        '--rdp-accent-color-dark': theme.palette.primary.dark,
                        '--rdp-accent-color-light': theme.palette.primary.light,
                        '--rdp-background-color': theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        padding: '0 8px 16px',
                      },
                      months: {
                        width: '100%',
                        maxWidth: '100%',
                        margin: '0 auto',
                      },
                      month: {
                        width: '100%',
                      },
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* event modal */}
            {mobileField === 'event' && (
              <Box>
                <List>
                  {EVENT_TYPES.map((et) => (
                    <ListItem key={et} button onClick={() => { setSearchData(prev => ({ ...prev, eventType: et })); setMobileOpen(false); setMobileField(null); }}>
                      <EventIcon style={{ width: 18, height: 18, marginRight: 12 }} />
                      <ListItemText primary={et} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Scoped CSS overrides for compact DayPicker */}
      <style>{`
      
        .rdp-day_disabled {
  color: #c2c8cc !important;
  cursor: not-allowed;
  opacity: 0.4;
}
  .rdp-selected .rdp-day_button, .rdp-selected .rdp-day_button:hover{
  background: #046242;
  color: #fff;
  font-weight: 400;
  }
  
  .rdp-day_button:hover{
    background: #f4f7f6;
  }
    .rdp-month_caption{
    font-weight: 400;
    border-bottom: 1px solid #D3D4D6;

    }
    .rdp-month_grid{
    width: 100%
    }
      `}</style>
    </Box>
  );
};

export default SearchBarReact;
